import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useRealtime } from './RealtimeContext';

const TestContext = createContext();

export function useTest() {
  return useContext(TestContext);
}

export function TestProvider({ children }) {
  const { currentUser } = useRealtime();
  const [currentTest, setCurrentTest] = useState(null);
  const [testProgress, setTestProgress] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isTestActive, setIsTestActive] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  const [visitedQuestions, setVisitedQuestions] = useState(new Set());

  // Auto-save answers to Firestore
  useEffect(() => {
    if (currentTest && currentUser && Object.keys(answers).length > 0) {
      const saveAnswers = async () => {
        try {
          const testProgressRef = doc(db, 'testProgress', `${currentUser.uid}_${currentTest.id}`);
          await updateDoc(testProgressRef, {
            answers,
            flaggedQuestions: Array.from(flaggedQuestions),
            visitedQuestions: Array.from(visitedQuestions),
            currentQuestionIndex,
            lastUpdated: new Date()
          });
        } catch (error) {
          if (error.code === 'permission-denied') {
            }
        }
      };

      const timeoutId = setTimeout(saveAnswers, 2000); // Debounce for 2 seconds
      return () => clearTimeout(timeoutId);
    }
  }, [answers, flaggedQuestions, visitedQuestions, currentQuestionIndex, currentTest, currentUser]);

  // Timer countdown
  useEffect(() => {
    if (isTestActive && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setIsTestActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isTestActive, timeRemaining]);

  const startTest = (test) => {
    setCurrentTest(test);
    setTimeRemaining(test.duration * 60); // Convert minutes to seconds
    setIsTestActive(true);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setFlaggedQuestions(new Set());
    setVisitedQuestions(new Set());
  };

  const endTest = () => {
    setIsTestActive(false);
    setTimeRemaining(0);
  };

  const saveAnswer = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const toggleFlagQuestion = (questionId) => {
    setFlaggedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const markQuestionVisited = (questionId) => {
    setVisitedQuestions(prev => new Set([...prev, questionId]));
  };

  const goToQuestion = (index) => {
    setCurrentQuestionIndex(index);
    if (currentTest?.questions?.[index]) {
      markQuestionVisited(currentTest.questions[index].id);
    }
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < currentTest?.questions?.length - 1) {
      goToQuestion(currentQuestionIndex + 1);
    }
  };

  const previousQuestion = () => {
    if (currentQuestionIndex > 0) {
      goToQuestion(currentQuestionIndex - 1);
    }
  };

  const loadTestProgress = async (testId) => {
    if (!currentUser) {
      return;
    }

    try {
      const testProgressRef = doc(db, 'testProgress', `${currentUser.uid}_${testId}`);
      const docSnap = await getDoc(testProgressRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        setAnswers(data.answers || {});
        setFlaggedQuestions(new Set(data.flaggedQuestions || []));
        setVisitedQuestions(new Set(data.visitedQuestions || []));
        setCurrentQuestionIndex(data.currentQuestionIndex || 0);
      }
    } catch (error) {
      if (error.code === 'permission-denied') {
        }
    }
  };

  const clearTestProgress = () => {
    setCurrentTest(null);
    setTestProgress({});
    setTimeRemaining(0);
    setIsTestActive(false);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setFlaggedQuestions(new Set());
    setVisitedQuestions(new Set());
  };

  const value = {
    currentTest,
    setCurrentTest,
    testProgress,
    timeRemaining,
    isTestActive,
    currentQuestionIndex,
    answers,
    flaggedQuestions,
    visitedQuestions,
    startTest,
    endTest,
    saveAnswer,
    toggleFlagQuestion,
    markQuestionVisited,
    goToQuestion,
    nextQuestion,
    previousQuestion,
    loadTestProgress,
    clearTestProgress
  };

  return (
    <TestContext.Provider value={value}>
      {children}
    </TestContext.Provider>
  );
}
